// src/padplanner/checksVocabulary.js
//
// The clearance checks' words and shapes (S5b): a run's verdict, a result's
// labels, the Checks tab's grouping. The server decides every verdict
// (admin-api/lib/padplanner/checks.js shapeRun); this only names it.
//
// ── NO RULES IS NEVER A PASS ────────────────────────────────────────────────
//
// verdictOf maps the server's verdict to one of five states, and anything it
// does not recognize to "stale", never to "pass": a pass is shown only when
// the server said pass. An org with no published rules is not_checked ("No
// clearance rules published — layout not checked"), and so is a plan with no
// run while the org has no rules. A plan with no run while the org has rules
// is stale: nothing has checked it.
import { LIMIT_WORDS, DISTANCE_RELATIONS } from "./ruleVocabulary";
import { lineTypeLabel } from "./lineVocabulary";

export const VERDICTS = Object.freeze(["pass", "warnings", "blocked", "stale", "not_checked"]);

export const NOT_CHECKED_TEXT = "No clearance rules published — layout not checked";
export const STALE_TEXT = "These results are out of date: the layout or the published rules changed since they ran.";
export const NO_RUN_TEXT = "This layout hasn't been checked against the published rules yet.";
export const NO_SUBJECTS_TEXT = "rule matched nothing";
export const RERUN_TEXT = "Re-run checks";
// The live reading while dragging, inside the defer band (D-S5-9).
export const DEFER_TEXT = "≈ — checked on save";
export const DEFER_WITHIN_FT = 0.1;

export const VERDICT_HEADLINES = Object.freeze({
  pass: "All checks pass",
  warnings: "Passes, with warnings",
  blocked: "Blocked",
  stale: "Checks out of date",
  not_checked: NOT_CHECKED_TEXT,
});
// Chrome tokens: status-ok for a pass, --warn-text (tokens.css; no Tailwind
// key of its own) for warnings, accent-red for a block.
export const VERDICT_TONES = Object.freeze({
  pass: "text-status-ok", warnings: "text-[rgb(var(--warn-text))]", blocked: "text-accent-red", stale: "text-fg-secondary", not_checked: "text-fg-secondary",
});

/** The run's verdict, from GET …/checks or a save's `checks`: one of VERDICTS, never "pass" unless the server said so. */
export function verdictOf({ run = null, rules = null } = {}) {
  if (run) return VERDICTS.includes(run.verdict) ? run.verdict : "stale";
  return rules === "none" ? "not_checked" : "stale";
}

/** Failing results on the tab: block and warn fails, from the run's counts. */
export function failingCount(run) {
  if (!run?.counts) return 0;
  return Number(run.counts.block_fail ?? 0) + Number(run.counts.warn_fail ?? 0);
}

/** The tab's label: "Checks · 1" with the failing count, as mockup 4 has it. */
export function checksTabLabel(checks) {
  const n = failingCount(checks?.run);
  return verdictOf(checks ?? {}) !== "not_checked" && n > 0 ? `Checks · ${n}` : "Checks";
}

/** A side's name: its label, else its template's name, else what it is (a line's type, 156). */
export function sideLabel(side) {
  if (!side) return null;
  return (typeof side.label === "string" && side.label.trim()) || side.template_name
    || (side.category === "line" ? lineTypeLabel(side.layer) : side.category) || "Unlabeled feature";
}

/**
 * The measured distance as a label shows it: whole feet, unless rounding
 * would put it on the passing side of the limit (99.998 against a min of
 * 100 is "99.99′", never "100′"): then hundredths, toward the failing side.
 */
export function measuredText(measured, limit, relation) {
  if (!Number.isFinite(measured)) return "—";
  const whole = Math.round(measured);
  const failing = relation === "min_distance" ? measured < limit : relation === "max_distance" ? measured > limit : null;
  const wholeLooksPassing = failing && (relation === "min_distance" ? whole >= limit : whole <= limit);
  if (!wholeLooksPassing) return `${whole}′`;
  const h = relation === "min_distance" ? Math.floor(measured * 100) / 100 : Math.ceil(measured * 100) / 100;
  return `${h.toFixed(2)}′`;
}

/** "84′ · min 100′" (mockup 4's witness label). */
export function witnessText(result) {
  const word = LIMIT_WORDS[result.relation];
  if (!word) return null;
  return `${measuredText(result.measured_ft, result.limit_ft, result.relation)} · ${word} ${Number(result.limit_ft)}′`;
}

/** One result's line on the tab. */
export function resultText(r) {
  const s = sideLabel(r.subject), o = sideLabel(r.object);
  if (r.outcome === "no_subjects") return NO_SUBJECTS_TEXT;
  if (DISTANCE_RELATIONS.includes(r.relation)) {
    if (!r.object) return `${s}: nothing within reach`;
    return `${s} ↔ ${o}`;
  }
  if (r.relation === "inside_perimeter") return r.outcome === "fail" ? `${s} is outside the perimeter` : `${s} is inside the perimeter`;
  if (r.relation === "no_overlap") return r.object ? `${s} overlaps ${o}` : `${s} overlaps nothing`;
  return s ?? r.rule_key;
}

/**
 * The tab's rows, one group per rule, in the server's order (failures first,
 * block before warn: the RESULTS query's ORDER BY). rulesById names each
 * rule's message; a rule no longer in a loaded set falls back to its key.
 */
export function groupByRule(results, rulesById = new Map()) {
  const groups = new Map();
  for (const r of results ?? []) {
    let g = groups.get(r.rule_id);
    if (!g) {
      const rule = rulesById.get(r.rule_id) ?? null;
      g = {
        rule_id: r.rule_id, rule_key: r.rule_key, relation: r.relation, severity: r.severity, limit_ft: r.limit_ft,
        message: rule?.message ?? null, fails: [], passes: 0, noSubjects: false,
      };
      groups.set(r.rule_id, g);
    }
    if (r.outcome === "fail") g.fails.push(r);
    else if (r.outcome === "pass") g.passes += 1;
    else if (r.outcome === "no_subjects") g.noSubjects = true;
  }
  return [...groups.values()];
}
